import React from "react";
import Select, { components as selectComponents } from "react-select";
import { withStyles } from "@material-ui/core/styles";
import createStyles from "@material-ui/core/styles/createStyles";
import { Theme } from "@material-ui/core/styles/createMuiTheme";
import Checkbox from "@material-ui/core/Checkbox";
import ProjectType from "../../../types/Project";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const PLACEHOLDER = "Sélectionnez les projets";

const styles = (theme: Theme) =>
  createStyles({
    root: {
      width: "100%"
    },
    label: {
      display: "block",
      fontSize: 16,
      fontWeight: 200
    },
    optionRow: {
      display: "flex",
      alignItems: "center",
      width: "100%"
    },
    optionLabel: {
      fontSize: 16,
      color: "#444f59"
    }
  });

type ProjectOption = {
  value: string;
  label: string;
};

interface Props {
  classes: Record<string, string>;
  label?: string;
  required?: boolean;
  projects: ProjectType[];
  datas: { projects: string[] };
  handleChange: (e: { target: { name: string; value: string[] } }) => void;
  disabled?: boolean;
  allProjects?: ProjectType[];
}

const formatProjectLabel = (project: ProjectType) =>
  `${project.name}${project.client ? ` /${project.client.name}` : ""}`;

const toOptions = (projects: ProjectType[]): ProjectOption[] =>
  projects.map((project) => ({
    value: project.id,
    label: formatProjectLabel(project)
  }));

const sortOptionsWithSelectedFirst = (
  options: ProjectOption[],
  selectedIds: string[]
): ProjectOption[] => {
  const selectedSet = new Set(selectedIds);
  const byId = new Map(options.map((option) => [option.value, option]));

  const selected = selectedIds
    .map((id) => byId.get(id))
    .filter((option): option is ProjectOption => option !== undefined);
  const unselected = options.filter((option) => !selectedSet.has(option.value));

  return [...selected, ...unselected];
};

const findProjectName = (
  id: string,
  projects: ProjectType[],
  allProjects?: ProjectType[]
) => {
  const project =
    projects.find((p) => p.id === id) ||
    (allProjects ? allProjects.find((p) => p.id === id) : undefined);
  return project ? formatProjectLabel(project) : null;
};

const Option = (props: any) => {
  const { isSelected, label } = props;

  return (
    <selectComponents.Option {...props}>
      <div className={props.selectProps.classes.optionRow}>
        <Checkbox
          checked={isSelected}
          tabIndex={-1}
          disableRipple
          color="default"
          style={{ padding: "0 8px 0 0" }}
        />
        <span className={props.selectProps.classes.optionLabel}>{label}</span>
      </div>
    </selectComponents.Option>
  );
};

const DropdownIndicator = (props: any) => (
  <selectComponents.DropdownIndicator {...props}>
    <img
      src="/assets/arrow-select.png"
      alt=""
      style={{ width: 12, marginRight: 8 }}
    />
  </selectComponents.DropdownIndicator>
);

const selectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    borderRadius: 4,
    minHeight: 40,
    border: "1px solid rgba(72,72,72,.2)",
    boxShadow: "0px 4px 1px rgba(0,0,0, 0.02)",
    fontSize: 16,
    color: "#444f59",
    backgroundColor: "#fff",
    cursor: "text",
    "&:hover": {
      border: "1px solid rgba(72,72,72,.2)"
    }
  }),
  valueContainer: (base: any) => ({
    ...base,
    padding: "2px 7px",
    flexWrap: "wrap"
  }),
  input: (base: any) => ({
    ...base,
    margin: 0,
    padding: "2px 0",
    fontSize: 16,
    color: "#444f59"
  }),
  placeholder: (base: any) => ({
    ...base,
    color: "#aaa",
    fontSize: 16
  }),
  multiValue: (base: any) => ({
    ...base,
    backgroundColor: "rgba(0,123,255,.12)",
    borderRadius: 3,
    margin: "2px 4px 2px 0"
  }),
  multiValueLabel: (base: any) => ({
    ...base,
    color: "#0056b3",
    fontSize: 13,
    padding: "2px 4px 2px 6px"
  }),
  multiValueRemove: (base: any) => ({
    ...base,
    color: "#0056b3",
    paddingRight: 4,
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "rgba(0,123,255,.25)",
      color: "#003d80"
    }
  }),
  menu: (base: any) => ({
    ...base,
    boxShadow: "0 2px 8px rgba(0,0,0,.15)",
    borderRadius: 4
  }),
  menuPortal: (base: any) => ({
    ...base,
    zIndex: 9999
  }),
  menuList: (base: any) => ({
    ...base,
    maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
    padding: 0
  }),
  option: (base: any, state: any) => ({
    ...base,
    fontSize: 16,
    backgroundColor: state.isFocused ? "rgba(0,0,0,.04)" : "#fff",
    color: "#444f59",
    cursor: "pointer",
    padding: "4px 12px"
  }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (base: any) => ({
    ...base,
    padding: 0
  }),
  clearIndicator: (base: any) => ({
    ...base,
    padding: "0 8px",
    color: "#aaa",
    cursor: "pointer",
    "&:hover": { color: "#666" }
  })
};

const ProjectMultiSelectAutocomplete = ({
  classes,
  label,
  required,
  projects,
  datas,
  handleChange,
  disabled = false,
  allProjects
}: Props) => {
  const selectedIds = datas.projects || [];
  const options = sortOptionsWithSelectedFirst(toOptions(projects), selectedIds);

  const selectedOptions: ProjectOption[] = selectedIds
    .map((id) => {
      const name = findProjectName(id, projects, allProjects);
      return name ? { value: id, label: name } : null;
    })
    .filter((option): option is ProjectOption => option !== null);

  return (
    <div className={classes.root}>
      {label && (
        <label className={classes.label}>
          {label}
          {required && (
            <span>
              {" "}
              <sup>*</sup>
            </span>
          )}
        </label>
      )}
      <div style={{ marginTop: label ? 8 : 0 }}>
        <Select
          isMulti
          name="projects"
          classes={classes}
          options={options}
          value={selectedOptions}
          onChange={(selected: any) => {
            handleChange({
              target: {
                name: "projects",
                value: selected
                  ? selected.map((option: ProjectOption) => option.value)
                  : []
              }
            });
          }}
          components={{
            Option,
            DropdownIndicator
          }}
          styles={selectStyles}
          isDisabled={disabled}
          isSearchable
          isClearable
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          placeholder={PLACEHOLDER}
          noOptionsMessage={() => "Aucun projet trouvé"}
          menuPortalTarget={
            typeof document !== "undefined" ? document.body : null
          }
          menuPosition="fixed"
        />
      </div>
    </div>
  );
};

ProjectMultiSelectAutocomplete.defaultProps = {
  disabled: false
};

export default withStyles(styles)(ProjectMultiSelectAutocomplete);
